import { ReactNode, useCallback, useState } from 'react';
import { createApp } from '@backstage/frontend-defaults';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  AppRootWrapperBlueprint,
  ThemeBlueprint,
} from '@backstage/plugin-app-react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import orgPlugin from '@backstage/plugin-org/alpha';
import githubActionsPlugin from '@backstage-community/plugin-github-actions/alpha';
import { catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import searchPlugin from '@backstage/plugin-search/alpha';
import { techDocsReportIssueAddonModule } from '@backstage/plugin-techdocs-module-addons-contrib/alpha';
import homePlugin from '@backstage/plugin-home/alpha';
import { UnifiedThemeProvider } from '@backstage/theme';
import { RiMoonFill, RiSunFill } from '@remixicon/react';
import { Progress } from '@backstage/core-components';
import { apisModule } from './extensions/apis';
import { iconsModule } from './extensions/icons';
import { pageLayoutModule } from './extensions/pageLayout';
import { signInModule } from './extensions/signInPage';
import { githubLandingPagePlugin } from '@internal/backstage-plugin-github-landingpage/alpha';
import {
  apiExplorerModule,
  catalogOverridesModule,
  xelerateCatalogPlugin,
} from '@internal/plugin-catalog/alpha';
import {
  createHubPlugin,
  scaffolderTemplatesModule,
} from '@internal/backstage-plugin-create-hub/alpha';
import { homeModule } from '@internal/plugin-home/alpha';
import {
  notificationsOverridesModule,
  userSettingsModule,
} from '@internal/plugin-user-settings/alpha';
import { announcementsTabPlugin } from '@internal/backstage-plugin-announcements/alpha';
import {
  confluencePlugin,
  searchOverridesModule,
} from '@internal/plugin-search/alpha';
import {
  catalogGraphPlugin,
  catalogImportOverridesModule,
  catalogUnprocessedEntitiesPlugin,
  devtoolsPlugin,
  mcpChatPage,
  mcpChatPlugin,
  notificationsPlugin,
  routesAlphaPages,
  SettingsPlugin,
} from './extensions/routes.alpha';
import { providerSettingsModule } from './extensions/providerSettings';
import {
  customUnifiedDarkTheme,
  customUnifiedLightTheme,
} from './components/theme/cariad-theme';
import { translationsModule } from './extensions/translations';
import { navModule } from './components/Root/Root';
import {
  RoleSetup,
  useGetUserInfo,
} from '@internal/backstage-plugin-user-role-setup-react';
import { useUserProfile } from '@backstage/plugin-user-settings';
import megatronWorkspacesPlugin from '@cariad-dxp/backstage-plugin-mega-workspaces/alpha';
import megatronPlugin from '@cariad-dxp/backstage-plugin-megatron/alpha';
import { gauthPlugin } from '@cariad-dxp/gauth-plugin';
import {
  documentationHubFrontendPlugin,
  techdocsPagesModule,
} from '@internal/backstage-plugin-documentation-hub/alpha';
import { dataContractApiDocsModule } from '@internal/backstage-plugin-datacontract/alpha';
import { scaffolderCustomFieldExtensionsPlugin } from '@internal/backstage-plugin-scaffolder-custom-field-extensions/alpha';
import { devtoolsAdminModule } from '@internal/backstage-plugin-devtools-admin-react/alpha';
import { GlobalHeader } from './components/global-header/GlobalHeader';
import { SignalsDisplay } from '@backstage/plugin-signals';
import authPlugin from '@backstage/plugin-auth';

const AppHeaderWrapper = ({ children }: { children?: ReactNode }) => {
  const [userInfoRefreshKey, setUserInfoRefreshKey] = useState(0);

  const configApi = useApi(configApiRef);

  // 1. Check if role selection feature flag is active
  const isRoleSelectionEnabled =
    configApi.getOptionalBoolean('features.enable-role-selection') ?? false;

  const { profile } = useUserProfile();

  // 2. Safe optional chaining: avoids crashing if profile hasn't initialized yet
  const roleFlowEmail = isRoleSelectionEnabled ? profile?.email : undefined;

  const {
    value: userInfo,
    loading: userInfoLoading,
    error: userInfoError,
  } = useGetUserInfo(roleFlowEmail, userInfoRefreshKey);

  const handleSubmitSuccess = useCallback(() => {
    setUserInfoRefreshKey(currentKey => currentKey + 1);
  }, []);

  // 3. Session Expired (401) -> Hard reload to trigger Backstage login screen
  if (userInfoError && (userInfoError as { status?: number })?.status === 401) {
    window.location.reload();
    return <Progress />;
  }

  // 4. Loading Gate: Only block on spinner while actively fetching AND no errors present
  const isStillLoading =
    isRoleSelectionEnabled && userInfoLoading && !userInfoError;

  if (isStillLoading) {
    return <Progress />;
  }

  // 5. Check for 504 NFS/Backend Timeout
  const is504Error = (userInfoError as { status?: number })?.status === 504;

  // 6. Role Setup Interception (if role is missing and no 504 timeout occurred)
  if (isRoleSelectionEnabled && userInfo && !userInfo.role && !is504Error) {
    return (
      <div
        style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}
      >
        <RoleSetup onSubmitSuccess={handleSubmitSuccess} />
      </div>
    );
  }

  // 7. Normal Main Application Shell
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <SignalsDisplay />
      <GlobalHeader />
      <div style={{ overflow: 'hidden', flex: 1 }}>{children}</div>
    </div>
  );
};

const headerModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    AppRootWrapperBlueprint.make({
      name: 'global-header-wrapper',
      params: { component: AppHeaderWrapper },
    }),
  ],
});

const lightThemeExtension = ThemeBlueprint.make({
  name: 'light',
  params: {
    theme: {
      id: 'light theme',
      title: 'Light Theme',
      variant: 'light',
      icon: <RiSunFill />,
      Provider: ({ children }: { children?: ReactNode }) => (
        <UnifiedThemeProvider
          theme={customUnifiedLightTheme}
          children={children}
        />
      ),
    },
  },
});

const darkThemeExtension = ThemeBlueprint.make({
  name: 'dark',
  params: {
    theme: {
      id: 'dark theme',
      title: 'Dark Theme',
      variant: 'dark',
      icon: <RiMoonFill />,
      Provider: ({ children }: { children?: ReactNode }) => (
        <UnifiedThemeProvider
          theme={customUnifiedDarkTheme}
          children={children}
        />
      ),
    },
  },
});

const themesModule = createFrontendModule({
  pluginId: 'app',
  extensions: [lightThemeExtension, darkThemeExtension],
});

// Official App Instance Creation
const app = createApp({
  features: [
    headerModule,
    navModule,
    // The Backstage UI plugin header of every page, in a frame that scrolls
    // below it; see packages/app/README.md.
    pageLayoutModule,
    iconsModule,
    apisModule,
    signInModule,
    // Home: the Xelerate dashboard and its widgets at `/`; visit tracking for
    // the recently and most visited widgets is enabled in app-config.yaml.
    homePlugin,
    homeModule,
    githubLandingPagePlugin,
    // Catalog: the Xelerate index page, My Assets, and the header, layouts,
    // cards and tabs of the core entity page; core cards and tabs we do not
    // show are disabled in app-config.yaml.
    xelerateCatalogPlugin,
    catalogOverridesModule,
    // The ownership card of users and the CI/CD tab, configured in
    // app-config.yaml.
    orgPlugin,
    githubActionsPlugin,
    createHubPlugin,
    documentationHubFrontendPlugin,
    // TechDocs: the DXP reader replaces the core reader page, the static docs
    // page and the reader header actions join the plugin, the Report Issue
    // addon joins the documentation-hub addons; `page:techdocs` and
    // `entity-content:techdocs` are disabled in app-config.yaml, the hub at
    // `/docs` mounts the TechDocs root route.
    techdocsPlugin,
    techdocsPagesModule,
    techDocsReportIssueAddonModule,
    // API docs: the Xelerate index filtered to APIs replaces the core API
    // explorer and moves it to `/apis` (`/api-docs` redirects there, see
    // `app/routes` in app-config.yaml); the data contract module adds the data
    // contract definition widget to the core `api:api-docs/config`.
    apiDocsPlugin,
    apiExplorerModule,
    dataContractApiDocsModule,
    announcementsTabPlugin,
    // Its result list item renders Confluence results on the search page, as
    // the announcements one renders announcements.
    confluencePlugin,
    scaffolderCustomFieldExtensionsPlugin,
    catalogGraphPlugin,
    catalogUnprocessedEntitiesPlugin,
    mcpChatPlugin,
    mcpChatPage,
    // Notifications: the Xelerate page replaces the core one at
    // `/notifications`; the header bell comes from the same package.
    notificationsPlugin,
    notificationsOverridesModule,
    themesModule,
    translationsModule,

    // Core Overrides Modules
    routesAlphaPages,
    // Search: the Xelerate page and client replace the core ones.
    searchPlugin,
    searchOverridesModule,
    catalogImportOverridesModule,
    // Scaffolder: the create-hub template list replaces the core one on
    // `/create/templates`; the core sub-page still serves the template wizard.
    scaffolderTemplatesModule,

    // DevTools at /devtools: Info and Config from the plugin, Unprocessed
    // Entities from catalogUnprocessedEntitiesPlugin, External Dependencies and
    // the admin pages (catalog health) from devtoolsAdminModule
    devtoolsPlugin,
    devtoolsAdminModule,
    // Settings: the General and Notifications tabs come from
    // plugins/user-settings, the auth provider list from the app.
    SettingsPlugin,
    userSettingsModule,
    providerSettingsModule,
    megatronWorkspacesPlugin,
    megatronPlugin,
    // gAuth at /gauth; the sidebar item is behind the `gauth-plugin` feature.
    gauthPlugin,
    authPlugin,
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(documentationHubFrontendPlugin.externalRoutes, {
      docRoot: techdocsPlugin.routes.docRoot,
    });
  },
});

export default app.createRoot();
